import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import {
  queryResearchData,
  exportResearchData,
  checkExportJobStatus,
  fetchExportJobs,
  downloadResearchExport,
  fetchChainOfCustody,
} from "../../../actions/researchDataActions";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { format } from "date-fns";

const ResearchDataExplorerPage = () => {
  const dispatch = useDispatch();

  // Filter state
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [completionStatus, setCompletionStatus] = useState("");
  const [sessionType, setSessionType] = useState("");
  const [platform, setPlatform] = useState("");
  const [subscriptionType, setSubscriptionType] = useState("");
  const [condition, setCondition] = useState("");

  // Data table state
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // MUI is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const searchIdRef = useRef(0);

  // Export jobs state
  const [exportJobs, setExportJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const pollingIntervalsRef = useRef({});
  const skipPaginationEffect = useRef(false);

  // Download state — tracks multiple concurrent downloads
  const [downloadingJobIds, setDownloadingJobIds] = useState(new Set());

  // Chain of custody
  const [cocOpen, setCocOpen] = useState(false);
  const [cocData, setCocData] = useState(null);
  const [isLoadingCoc, setIsLoadingCoc] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Format COC data as plain text for copy/download
  const formatCocAsText = useCallback((data) => {
    if (!data) return "";
    const lines = [
      "============================================================",
      "CHAIN OF CUSTODY DOCUMENTATION",
      "Master Data Export for Research Publications",
      "============================================================",
      "",
      `Generated:        ${data.generated_at || "N/A"}`,
      `Export Job ID:     ${data.job_id || "N/A"}`,
      `Export Type:       ${data.job_type || "N/A"}`,
      `Code Version:      ${data.code_version || "N/A"} (${data.code_version_full || "N/A"})`,
      "",
      "DATE RANGE",
      "----------",
      `From:  ${data.date_range?.from || "N/A"}`,
      `To:    ${data.date_range?.to || "N/A"}`,
      "",
      "DATA SUMMARY",
      "------------",
      `Total Rows:      ${data.data_summary?.total_rows?.toLocaleString() || "N/A"}`,
      `Unique Users:    ${data.data_summary?.unique_users?.toLocaleString() || "N/A"}`,
      "",
      "FILE INTEGRITY",
      "--------------",
      `File Name:       ${data.file_integrity?.file_name || "N/A"}`,
      `CSV SHA-256:     ${data.file_integrity?.csv_sha256 || "N/A"}`,
      `GCS Key:         ${data.file_integrity?.gcs_key || "N/A"}`,
      `BigQuery Loaded: ${data.file_integrity?.bigquery_loaded ? "Yes" : "No"}`,
      "",
      "ANONYMIZATION",
      "-------------",
      `User IDs:        ${data.anonymization?.user_ids || "N/A"}`,
      `PII Fields:      ${data.anonymization?.pii_fields || "N/A"}`,
      "",
      "COLUMNS EXPORTED",
      "----------------",
      data.columns?.join(", ") || "N/A",
    ];

    if (data.known_caveats?.length > 0) {
      lines.push("", "KNOWN CAVEATS", "-------------");
      data.known_caveats.forEach((caveat, i) => {
        lines.push(`${i + 1}. ${caveat}`);
      });
    }

    lines.push("", "============================================================");
    return lines.join("\n");
  }, []);

  const handleCopyCoc = useCallback(() => {
    const text = formatCocAsText(cocData);
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({ open: true, message: "Chain of custody copied to clipboard", severity: "success" });
    });
  }, [cocData, formatCocAsText]);

  const handleDownloadCoc = useCallback(() => {
    const text = formatCocAsText(cocData);
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chain_of_custody_job_${cocData?.job_id || "unknown"}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [cocData, formatCocAsText]);

  // Load export jobs on mount
  useEffect(() => {
    loadExportJobs();

    return () => {
      Object.values(pollingIntervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, []);

  const isValidDate = d => d instanceof Date && !isNaN(d);

  const buildFilters = () => {
    const filters = {};
    if (dateFrom && isValidDate(dateFrom)) filters.date_from = format(dateFrom, "yyyy-MM-dd");
    if (dateTo && isValidDate(dateTo)) filters.date_to = format(dateTo, "yyyy-MM-dd");
    if (completionStatus) filters.completion_status = completionStatus;
    if (sessionType) filters.session_type = sessionType;
    if (platform) filters.platform = platform;
    if (subscriptionType) filters.subscription_type = subscriptionType;
    if (condition) filters.condition = condition;
    return filters;
  };

  const handleSearch = async (overridePage) => {
    const searchPage = overridePage !== undefined ? overridePage : page;
    const currentSearchId = ++searchIdRef.current;
    setIsLoadingData(true);
    try {
      const filters = buildFilters();
      const result = await dispatch(queryResearchData(filters, searchPage + 1, rowsPerPage));
      if (currentSearchId !== searchIdRef.current) return; // stale response
      if (result) {
        setRows(result.data || []);
        setTotalCount(result.total_count || 0);
      }
    } catch (error) {
      if (currentSearchId !== searchIdRef.current) return;
      console.error("Failed to query research data:", error);
      setSnackbar({ open: true, message: "Failed to query data. Please try again.", severity: "error" });
    } finally {
      if (currentSearchId === searchIdRef.current) setIsLoadingData(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Re-fetch when page or rowsPerPage changes (only if we've done an initial search)
  useEffect(() => {
    if (skipPaginationEffect.current) {
      skipPaginationEffect.current = false;
      return;
    }
    if (rows.length > 0 || page > 0) {
      handleSearch(page);
    }
  }, [page, rowsPerPage]);

  const handleExportCsv = async () => {
    try {
      const filters = buildFilters();
      const job = await dispatch(exportResearchData(filters));

      if (job && job.job_id) {
        // Optimistic update
        setExportJobs(prev => [job, ...prev]);
        startPolling(job.job_id);

        setSnackbar({
          open: true,
          message: "Export started. Check the Past Exports section below.",
          severity: "success",
        });

        loadExportJobs(true);
      }
    } catch (error) {
      console.error("Failed to start export:", error);
      setSnackbar({ open: true, message: "Failed to start export. Please try again.", severity: "error" });
    }
  };

  const loadExportJobs = async (preserveOptimistic = false) => {
    setIsLoadingJobs(true);
    try {
      const jobs = await dispatch(fetchExportJobs());
      if (Array.isArray(jobs)) {
        setExportJobs(prev => {
          if (preserveOptimistic) {
            const serverJobIds = new Set(jobs.map(j => j.job_id));
            const optimisticJobs = prev.filter(j => !serverJobIds.has(j.job_id));
            return [...optimisticJobs, ...jobs];
          }
          return jobs;
        });

        // Start polling for in-progress jobs
        jobs.forEach(job => {
          if (
            (job.status === "pending" || job.status === "processing") &&
            !pollingIntervalsRef.current[job.job_id]
          ) {
            startPolling(job.job_id);
          }
        });
      }
    } catch (error) {
      console.error("Failed to load export jobs:", error);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const pollingFailuresRef = useRef({});

  const startPolling = jobId => {
    if (pollingIntervalsRef.current[jobId]) return;
    pollingFailuresRef.current[jobId] = 0;

    const interval = setInterval(async () => {
      try {
        const job = await dispatch(checkExportJobStatus(jobId));
        if (job) {
          pollingFailuresRef.current[jobId] = 0;
          setExportJobs(prev => prev.map(j => (j.job_id === jobId ? job : j)));
          if (job.status === "completed" || job.status === "failed") {
            stopPolling(jobId);
          }
        } else {
          pollingFailuresRef.current[jobId] = (pollingFailuresRef.current[jobId] || 0) + 1;
          if (pollingFailuresRef.current[jobId] >= 5) {
            console.error(`Polling stopped for job ${jobId} after 5 consecutive failures`);
            stopPolling(jobId);
          }
        }
      } catch (error) {
        console.error(`Failed to check status for job ${jobId}:`, error);
        stopPolling(jobId);
      }
    }, 5000);

    pollingIntervalsRef.current[jobId] = interval;
  };

  const stopPolling = jobId => {
    if (pollingIntervalsRef.current[jobId]) {
      clearInterval(pollingIntervalsRef.current[jobId]);
      delete pollingIntervalsRef.current[jobId];
    }
  };

  const handleDownload = async (jobId) => {
    setDownloadingJobIds(prev => new Set(prev).add(jobId));
    try {
      await dispatch(downloadResearchExport(jobId));
    } finally {
      setDownloadingJobIds(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case "pending": return "warning";
      case "processing": return "info";
      case "completed": return "success";
      case "failed": return "error";
      default: return "default";
    }
  };

  const formatDateTime = dateString => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const handleViewChainOfCustody = async jobId => {
    setIsLoadingCoc(true);
    setCocOpen(true);
    try {
      const data = await dispatch(fetchChainOfCustody(jobId));
      setCocData(data);
    } catch (error) {
      console.error("Failed to fetch chain of custody:", error);
      setSnackbar({ open: true, message: "Failed to load chain of custody.", severity: "error" });
      setCocOpen(false);
    } finally {
      setIsLoadingCoc(false);
    }
  };

  // Get column headers from first row
  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

  return (
    <MainContentContainer>
      <AppBar position="sticky" sx={{ width: "100%" }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Research Data Explorer
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: 3 }}>
        {/* Filter Panel */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date From"
                value={dateFrom}
                onChange={setDateFrom}
                slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
              />
              <DatePicker
                label="Date To"
                value={dateTo}
                onChange={setDateTo}
                slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
              />
            </LocalizationProvider>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Completion Status</InputLabel>
              <Select
                value={completionStatus}
                label="Completion Status"
                onChange={e => setCompletionStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="abandoned">Abandoned</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platform}
                label="Platform"
                onChange={e => setPlatform(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="iOS">iOS</MenuItem>
                <MenuItem value="Android">Android</MenuItem>
                <MenuItem value="Web">Web</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Session Type"
              size="small"
              value={sessionType}
              onChange={e => setSessionType(e.target.value)}
              sx={{ width: 160 }}
            />

            <TextField
              label="Subscription Type"
              size="small"
              value={subscriptionType}
              onChange={e => setSubscriptionType(e.target.value)}
              sx={{ width: 160 }}
            />

            <TextField
              label="Condition"
              size="small"
              value={condition}
              onChange={e => setCondition(e.target.value)}
              sx={{ width: 180 }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="contained" color="primary" onClick={() => { if (page !== 0) { skipPaginationEffect.current = true; setPage(0); } handleSearch(0); }}>
              Search
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleExportCsv}>
              Export CSV
            </Button>
          </Box>
        </Paper>

        {/* Data Table */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Results {totalCount > 0 && `(${totalCount.toLocaleString()} rows)`}
          </Typography>
          {isLoadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              No data found. Apply filters and click Search to query BigQuery.
            </Typography>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map(col => (
                        <TableCell key={col} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={idx} hover>
                        {columns.map(col => (
                          <TableCell key={col} sx={{ whiteSpace: "nowrap", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row[col] !== null && row[col] !== undefined ? String(row[col]) : ""}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[100, 250, 500, 1000]}
              />
            </>
          )}
        </Paper>

        {/* Past Exports */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Past Exports
          </Typography>
          {isLoadingJobs ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : exportJobs.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              No exports yet. Use the Export CSV button above to generate an export.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Rows</TableCell>
                    <TableCell>Unique Users</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exportJobs.map(job => (
                    <TableRow key={job.job_id}>
                      <TableCell>{job.job_id}</TableCell>
                      <TableCell>
                        <Chip label={job.status} color={getStatusColor(job.status)} size="small" />
                      </TableCell>
                      <TableCell>{job.row_count != null ? job.row_count.toLocaleString() : "—"}</TableCell>
                      <TableCell>{job.unique_users != null ? job.unique_users.toLocaleString() : "—"}</TableCell>
                      <TableCell>{formatDateTime(job.created_at)}</TableCell>
                      <TableCell>{job.completed_at ? formatDateTime(job.completed_at) : "—"}</TableCell>
                      <TableCell>
                        {job.status === "completed" ? (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleDownload(job.job_id)}
                              disabled={downloadingJobIds.has(job.job_id)}
                              startIcon={downloadingJobIds.has(job.job_id) ? <CircularProgress size={16} /> : null}
                            >
                              {downloadingJobIds.has(job.job_id) ? "Preparing…" : "Download"}
                            </Button>
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => handleViewChainOfCustody(job.job_id)}
                            >
                              Chain of Custody
                            </Button>
                          </Box>
                        ) : job.status === "failed" ? (
                          <Typography variant="caption" color="error">
                            {job.error_message || "Export failed"}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            In progress...
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Chain of Custody Dialog */}
      <Dialog open={cocOpen} onClose={() => setCocOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Chain of Custody Documentation</DialogTitle>
        <DialogContent dividers>
          {isLoadingCoc ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : cocData ? (
            <Box sx={{ fontFamily: "monospace", fontSize: 13 }}>
              <Typography variant="subtitle2" gutterBottom>General</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>Job ID</TableCell><TableCell>{cocData.job_id}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>Job Type</TableCell><TableCell>{cocData.job_type}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>Generated At</TableCell><TableCell>{cocData.generated_at}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>Code Version</TableCell><TableCell>{cocData.code_version} ({cocData.code_version_full})</TableCell></TableRow>
                </TableBody>
              </Table>

              <Typography variant="subtitle2" gutterBottom>Date Range</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>From</TableCell><TableCell>{cocData.date_range?.from}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>To</TableCell><TableCell>{cocData.date_range?.to}</TableCell></TableRow>
                </TableBody>
              </Table>

              <Typography variant="subtitle2" gutterBottom>Data Summary</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>Total Rows</TableCell><TableCell>{cocData.data_summary?.total_rows?.toLocaleString()}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>Unique Users</TableCell><TableCell>{cocData.data_summary?.unique_users?.toLocaleString()}</TableCell></TableRow>
                </TableBody>
              </Table>

              <Typography variant="subtitle2" gutterBottom>File Integrity</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>File Name</TableCell><TableCell>{cocData.file_integrity?.file_name}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>CSV SHA-256</TableCell><TableCell sx={{ wordBreak: "break-all" }}>{cocData.file_integrity?.csv_sha256 || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>GCS Key</TableCell><TableCell sx={{ wordBreak: "break-all" }}>{cocData.file_integrity?.gcs_key || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>BigQuery Loaded</TableCell><TableCell>{cocData.file_integrity?.bigquery_loaded ? "Yes" : "No"}</TableCell></TableRow>
                </TableBody>
              </Table>

              <Typography variant="subtitle2" gutterBottom>Anonymization</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>User IDs</TableCell><TableCell>{cocData.anonymization?.user_ids}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: "bold" }}>PII Fields</TableCell><TableCell>{cocData.anonymization?.pii_fields}</TableCell></TableRow>
                </TableBody>
              </Table>

              <Typography variant="subtitle2" gutterBottom>Columns Exported</Typography>
              <Box sx={{ mb: 2, p: 1, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-word" }}>
                  {cocData.columns?.join(", ")}
                </Typography>
              </Box>

              {cocData.known_caveats && cocData.known_caveats.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Known Caveats</Typography>
                  <Box sx={{ mb: 2, pl: 2 }}>
                    {cocData.known_caveats.map((caveat, i) => (
                      <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                        - {caveat}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">No data available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {cocData && (
            <>
              <Button onClick={handleCopyCoc} size="small">
                Copy to Clipboard
              </Button>
              <Button onClick={handleDownloadCoc} size="small">
                Download .txt
              </Button>
            </>
          )}
          <Button onClick={() => setCocOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContentContainer>
  );
};

export default ResearchDataExplorerPage;
